import { Canvas, useThree } from '@react-three/fiber'
import { Environment, OrbitControls, Plane, Box } from '@react-three/drei'
import { Suspense, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { Leva } from 'leva'

import { Pathtracer, usePathtracer } from '@react-three/gpu-pathtracer'

import Controls from './Controls'

const rotateBoxes = window.fxrand() < 0.4
let randomCameraAngle = window.fxrand() * Math.PI * 2 - Math.PI / 4
let randomCameraHeight = 14 + 14 * Math.floor(window.fxrand())
const randomRotation = () => (rotateBoxes ? window.fxrand() : 0)

const hexValues = '0123456789ABCDEF'.split('')
export const randomHEX = () => {
  let hex = '#'
  for (let h = 0; h < 6; h++) {
    hex += hexValues[Math.floor(window.fxrand() * hexValues.length)]
  }
  return hex
}
const randomHSL = (padH, rangeH, padS, rangeS, padL, rangeL) => {
  return `hsl(${Math.round(padH + window.fxrand() * rangeH)},${Math.round(
    padS + window.fxrand() * rangeS
  )}%,${Math.round(padL + window.fxrand() * rangeL)}%)`
}

const huePad = Math.floor(window.fxrand() * 360)
const floorMetalness = Math.floor(window.fxrand() * 10) / 10
const floorRoughness = Math.floor(window.fxrand() * 3) / 10

const glassType = Math.floor(window.fxrand() * 3)
const glassFat = window.fxrand() < 0.7
if (glassFat === undefined && glassType === undefined) {
}
const singleColor = true
const desaturated = singleColor && window.fxrand() < 0.4
const clear = desaturated && window.fxrand() < 0.3

const floorColor = randomHSL(180, 180 + 70, 0, 50, 20, 80)
const light = window.fxrand() < 0.1
const backgroundIntensity = light ? Math.floor(11 + window.fxrand() * 0) : 2
const glassDensity = Math.floor(window.fxrand() * 3) / 100

const lsDirection = window.fxrand() < 0.3
const both = window.fxrand() < 0.1
const features = {
  lightSource: both ? 'hv' : lsDirection ? 'ho' : 've',
  floorMetalness: Math.floor((floorMetalness * 10) / 3) / 10,
  floorRoughness: floorRoughness,
  glass: desaturated ? (clear ? 'clear' : 'desat') : 'satur',
  light: light ? 'ON' : 'OFF',
  density: glassDensity,
}
console.table(features)
window.$fxhashFeatures = features


let w = 6,
  h = 3,
  d = 6

let width = Math.floor(2 + window.fxrand() * w)
let height = Math.floor(2 + window.fxrand() * h)
let depth = Math.floor(2 + window.fxrand() * d)
let glassColor = randomHSL(huePad, singleColor ? 10 : 360, desaturated ? 0 : 60, 0, clear ? 100 : 30, 70)

const randomRotations = [
  randomRotation(),
  randomRotation(),
  randomRotation(),
]
const laserColors = [
  randomHSL(0, 360, 0, 100, 50, 30),
]
if(both){
  laserColors.push(randomHSL(0, 360, 0, 100, 50, 30))
}

function Floor() {
  return (
    <>
      <Plane args={[1800, 1600]} position={[-10, 0, 50]} rotation-x={-Math.PI / 2}>
        <meshPhysicalMaterial metalness={floorMetalness} roughness={floorRoughness} color={floorColor} />
      </Plane>
      {both ? (
        <>
          <Box args={[0.1, 0.1, 500]} position={[0, 1, 0]} rotation={[Math.PI / 2, Math.PI / 2, 0]} visible={false}>
            <meshPhongMaterial emissive={laserColors[0]} emissiveIntensity={10} />
          </Box>
          <Box args={[0.1, 0.1, 500]} position={[0, 3, 0]} rotation={[Math.PI / 2, 0, 0]} visible={false}>
            <meshPhongMaterial emissive={laserColors[1]} emissiveIntensity={10} />
          </Box>
        </>
      ) : lsDirection ? (
        <Box args={[0.1, 0.1, 500]} position={[0, 1, 0]} rotation={[Math.PI / 2, Math.PI / 2, 0]} visible={false}>
          <meshPhongMaterial emissive={laserColors[0]} emissiveIntensity={10} />
        </Box>
      ) : (
        <Box args={[0.1, 0.1, 500]} position={[0, 3, 0]} rotation={[Math.PI / 2, 0, 0]} visible={false}>
          <meshPhongMaterial emissive={laserColors[0]} emissiveIntensity={10} />
        </Box>
      )}
    </>
  )
}
function Thing({ setEnabled }) {
  const { clear, update } = usePathtracer()
  const gl = useThree((state) => state.gl)
  useEffect(() => {
    window.takeScreenshot = () => {
      const link = document.createElement('a')
      link.setAttribute('download', `beam_${window.fxhash}.png`)
      link.setAttribute('href', gl.domElement.toDataURL('image/png').replace('image/png', 'image/octet-stream'))
      link.click()
    }
  }, [gl])
  const camera = useThree((state) => state.camera)

  useLayoutEffect(() => update(), [])
  useLayoutEffect(() => {
    camera.lookAt(0, 0, 0)
  }, [camera])

  return (
    <>
      <OrbitControls
        onEnd={() => setEnabled(true)}
        onStart={() => setEnabled(false)}
        onChange={() => clear()}
        dampingFactor={1}
        zoomSpeed={1}
        screenSpacePanning={true}
      />
      <group>
        <Floor />
      </group>
    </>
  )
}
export default function App() {
  const opts = Controls()
  const paused = !opts.Rendering
  const cameraPosition = useMemo(() => {
    let radius = 16
    return {
      x: Math.floor(radius * Math.cos(randomCameraAngle)),
      y: Math.floor(radius * Math.sin(randomCameraAngle)),
      h: randomCameraHeight,
    }
  }, [])

  const renderedBoxes = useMemo(
    () => (
      <>
        <Box
          args={[width, height, depth]}
          position={[0, height / 2, 0]}
          rotation={[Math.PI * 2 * randomRotations[0], Math.PI * 2 * randomRotations[0], Math.PI * 2 * randomRotations[0]]}
          castShadow
          receiveShadow
          dispose={null}
        >
          <meshPhysicalMaterial
            clearcoat={1}
            clearcoatRoughness={0}
            color={glassColor}
            transmission={1}
            thickness={0.5}
            roughness={glassDensity * 2}
          />
        </Box>
      </>
    ),
    []
  )

  const [enabled, setEnabled] = useState(true)
  const [hiddenControls, setHiddenControls] = useState(true)
  const hideControls = () => {
    setHiddenControls(true)
  }
  const showControls = () => {
    setHiddenControls(false)
  }
  return (
    <>
      <span onMouseLeave={hideControls}>
        <Leva
          // collapsed
          titleBar={{
            title: '',
            filter: false,
          }}
          hidden={hiddenControls}
          flat={true}
        />
      </span>
      <Canvas
        camera={{
          position: [cameraPosition.x, cameraPosition.h, cameraPosition.y],
          fov: 40,
        }}
        gl={{
          preserveDrawingBuffer: true,
        }}
        onMouseEnter={showControls}
      >
        <Suspense fallback={null}>
          <Pathtracer
            background={{
              type: 'Gradient',
              top: '#fff',
              bottom: '#000',
              intensity: backgroundIntensity,
            }}
            bounces={opts.Rendering_Bounces}
            enabled={enabled}
            paused={paused}
            samples={opts.Rendering_Samples}
            resolutionScale={opts.Rendering_Scale}
          >
            <Environment preset="night" background={false} />
            <Thing setEnabled={setEnabled} />

            {renderedBoxes}
          </Pathtracer>
        </Suspense>
        {/* <Stats /> */}
      </Canvas>
    </>
  )
}
